<template>
  <div id="app" :class="{ 'modal-open': cookieWall }">
    <SmartNavBar
      v-if="showNavBar"
      :class="navClass"
      :brand-logo="brandLogo"
      :brand-mobile-logo="brandMobileLogo"
      :brand-name="brandName"
      :show-tagline="showTagLine"
      :show-about-us="false"
      :phone-icon="whitelabelPhoneIcon"
      @click:about="openAboutModal"
    >
      <img
        slot="phoneIcon"
        :src="require(`@/assets/icons/${mobileCallIcon}`)"
        class="phone-icon"
        alt="Bionic phone"
      >
    </SmartNavBar>
    <div class="page">
      <transition name="slide-fade" mode="out-in">
        <router-view />
      </transition>
    </div>
    <CookieConsent
      v-if="brandCookieWall"
      @close-cookie-wall="closeCookieWall"
    />
    <modals-container />
    <DevBox v-if="!isProduction" />
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState, mapActions, mapGetters } from 'vuex'
import SmartNavBar from '@mic/frontend/navigation/SmartNavBar'
import Routable from '@mic/frontend/mixins/Routable'
import Raygun from '@mic/frontend/mixins/Raygun'
import DevBox from '@mic/frontend/DevBox/DevBox'
import NavigationGuardsDevBox from '@mic/frontend/DevBox/NavigationGuardsDevBox'
import CookieConsent from './components/cookies/CookieConsent'
import Cookies from 'js-cookie'
import Utag from '@/mixins/Utag'

const whitelabel = require('../whitelabel.config')

export default {
  components: {
    SmartNavBar,
    DevBox,
    CookieConsent
  },

  devBoxComponents: [NavigationGuardsDevBox],

  mixins: [Raygun, Routable, Utag],

  data () {
    return {
      debouncedHandleResize: debounce(this.handleResize, 250, {
        leading: true
      }),
      brandLogo: whitelabel[process.env.VUE_APP_BRAND].logo,
      brandMobileLogo: whitelabel[process.env.VUE_APP_BRAND].mobileLogo,
      brandName: whitelabel[process.env.VUE_APP_BRAND].name,
      mobileCallIcon: whitelabel[process.env.VUE_APP_BRAND].callIcon,
      brandCookieWall: whitelabel[process.env.VUE_APP_BRAND].cookieWall,
      whitelabelPhoneIcon: whitelabel[process.env.VUE_APP_BRAND].navPhoneIcon,
      cookieWall: false,
      hideNavOnConfirmationAndError:
        whitelabel[process.env.VUE_APP_BRAND].hideNavOnConfirmationAndError
    }
  },

  computed: {
    ...mapState({
      leadStatus: (state) => state.CallBack.status,
      contactDateTime: (state) => state.CallBack.contactDateTime
    }),
    ...mapGetters(['isMobile']),
    navClass () {
      return { 'nav-bar--homepage': this.onPage('home') }
    },
    isProduction () {
      return process.env.NODE_ENV === 'production'
    },
    showNavBar () {
      if (!this.hideNavOnConfirmationAndError) return true
      if (this.onPage(['confirmation', 'error'])) return false
      return true
    },
    showTagLine () {
      if (this.isMobile) return false
      return true
    }
  },

  watch: {
    leadStatus (newStatus) {
      if (!['success', 'error'].includes(newStatus)) return
      const success = newStatus === 'success'
      const name = this.contactDateTime
        ? 'voip-journey-schedule'
        : 'voip-journey-call-now'

      this.gtmTagging(name, success)
    }
  },

  created () {
    if (
      !this.$store.state.Dev ||
      !this.$store.state.Dev.disableCampaignService
    ) {
      this.$store.dispatch('fetchCampaign', {
        referrerURL:
          this.$route.query.current_url ||
          document.referrer ||
          document.location.origin,
        currentURL: this.$route.query.url_referer || document.location.href,
        googleR: this.$route.query.r
      })
      // Start session in sessions warehouse
      this.$store.dispatch('startSession', {
        guid: this.$store.getters.GUID
      })
    }
  },

  mounted () {
    window.document.title = whitelabel[process.env.VUE_APP_BRAND].appTitle
    if (!Cookies.get('cookie_consent')) {
      this.cookieWall = true
    }
    this.initResize()
    if (this.$route.query.affclie) this.setMaskCode(this.$route.query.affclie)
    if (this.$route.query.AFFCLIE) this.setMaskCode(this.$route.query.AFFCLIE)
    this.actionSetTestGroup()
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.debouncedHandleResize)
  },

  methods: {
    ...mapActions(['actionSetTestGroup']),
    setMaskCode (affclie) {
      this.$store.dispatch('actionUpdateCallBackData', { maskCode: affclie })
    },
    closeCookieWall () {
      this.cookieWall = false
    },
    handleResize () {
      this.$store.commit('SET_WINDOW_SIZE', {
        width: window.innerWidth,
        height: window.innerHeight
      })
    },

    initResize () {
      this.handleResize()
      window.addEventListener('resize', this.debouncedHandleResize)
    },

    openAboutModal () {
      this.$modal.show('about-us')
    }
  }
}
</script>
<style lang="scss" scoped>
.slide-fade-enter-active {
  transition: all .3s ease;
}
.slide-fade-leave-active {
  transition: all .4s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
<style lang="scss">
#app {
  font-family: $font-family;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: $text-color;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;

  .nav-bar {
    padding-bottom: 0;
    height: 48px;

    @media (min-width: $screen-md) {
      height: 87px;
    }

    .phone-icon {
      margin-left: auto;
       @media (min-width: $screen-md) {
        display: none;
      }
    }
  }

  input.mic-input__input-container--error {
    border: 1px solid $error-red;
  }

  .phone-number {
    span.number {
      color: $bionic-navy;

      @media(min-width: 800px) {
        font-size: 26px;
      }
    }

    .phone-number__opening-hours {
      color: $bionic-navy;
      font-weight: normal;
    }
  }

  .link-wrap,
  .logo-wrap {
    @media (min-width: 800px) {
      font-size: 18px;
    }
    div.dividing-line {
      @media(min-width: 800px) {
        height: 41px;
      }
    }
  }

  .link-wrap {
    div.dividing-line {
      display: none;
      @media (min-width: $screen-md) {
        display: block;
        height: 100%;
        background: #d8d8d8;
      }
    }
  }
}

body {
  font-size: 18px;
  line-height: 26px;
  background-color: $page-bg;
  display: flex;
}

a {
  color: inherit;

  &:hover {
    color: inherit;
  }
}

.container {
  padding-left: $spacing-16;
  padding-right: $spacing-16;
}

@media (min-width: 1200px) {
  .container {
    max-width: 1110px;
  }
}

.small-container {
  max-width: 760px;
  margin: $spacing-0 auto;
}

h1 {
  font-family: $font-family-bold;
  font-size: $h1-font-size;
  color: $h1-font-colour;
}

h2 {
  font-size: 42px;
  line-height: 47px;
  font-weight: 900;
  margin: $spacing-0;
  text-align: left;
}

h3 {
  font-size: 26px;
  line-height: 32px;
  font-weight: bold;
  margin: $spacing-0;
  text-align: left;
}

h4 {
  font-size: 18px;
  line-height: 26px;
  font-weight: 500;
  margin: $spacing-0;
  text-align: left;
}

.section-header {
  margin-bottom: $spacing-24;
  text-align: left;
  font-size: 30px;
  line-height: 37px;

  @media (min-width: $screen-md) {
    text-align: center;
    font-size: 40px;
    line-height: 44px;
  }
}

@media (min-width: $screen-md) {
  .v--modal-block-scroll {
    padding-right: 15px;
  }
}
</style>

<style lang="scss" scoped>
.page {
  position: relative;
  z-index: 100;
  background-color: $page-bg;
  overflow: hidden;
  flex-grow: 1;
  margin-top: -$spacing-16;
}

.rounded-page {
  border-radius: $border-radius-large $border-radius-large 0 0;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - #{$nav-height});

  @media (max-width: $screen-md) {
    margin-top: -$spacing-8;
    border-radius: $border-radius-medium $border-radius-medium 0 0;
  }
}
</style>
